import React, { useState } from 'react';
import styled from 'styled-components';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import LoadingScreen from './LoadingScreen';

// Styled components for admin layout
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f7fb;
  position: relative;
`;

const AdminSidebar = styled.div`
  width: 280px;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1200;
  top: 0;
  right: 0;
  transition: transform 0.3s ease;
  transform: translateX(${props => (props.open ? '0' : '100%')});
  @media (min-width: 992px) {
    transform: none;
    right: 0;
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
`;

const AdminTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 1rem 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin: 0.25rem 0;
`;

const NavLink = styled(Link)`
  display: block;
  padding: 0.75rem 1.5rem;
  color: #555;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f7fb;
    color: #333;
  }
`;

const LogoutButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  text-align: left;
  font-size: 1rem;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f7fb;
    color: #333;
  }
`;

const SidebarBackdrop = styled.div`
  display: ${props => (props.open ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.25);
  z-index: 1100;
  transition: opacity 0.3s;
  @media (min-width: 992px) {
    display: none;
  }
`;

const SidebarToggle = styled.button`
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1300;
  background: #fff;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  @media (min-width: 992px) {
    display: none;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 2rem;
  width: 100%;
  margin-right: 0;
  transition: margin-right 0.3s;
  @media (min-width: 992px) {
    margin-right: 280px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const PageDescription = styled.p`
  color: #666;
  font-size: 1rem;
  max-width: 600px;
`;

const AdminLayout = ({ title, description }) => {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleBackdropClick = () => {
    setSidebarOpen(false);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!admin) {
    return null; // AdminProtectedRoute will handle redirection
  }

  return (
    <LayoutContainer>
      <SidebarBackdrop open={sidebarOpen} onClick={handleBackdropClick} />
      <SidebarToggle onClick={handleSidebarToggle} aria-label="Toggle sidebar">
        <svg width="24" height="24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
      </SidebarToggle>
      <AdminSidebar open={sidebarOpen}>
        <SidebarHeader>
          <AdminTitle>Admin Panel</AdminTitle>
        </SidebarHeader>
        <NavMenu>
          <NavItem>
            <NavLink to="/admin/dashboard" onClick={()=>setSidebarOpen(false)}>Dashboard</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/users" onClick={()=>setSidebarOpen(false)}>Users</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/orders" onClick={()=>setSidebarOpen(false)}>Orders</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/products" onClick={()=>setSidebarOpen(false)}>Products</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/landing-pages" onClick={()=>setSidebarOpen(false)}>Landing Pages</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/landing-page-edits" onClick={()=>setSidebarOpen(false)}>Landing Page Edits</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/audit-logs" onClick={()=>setSidebarOpen(false)}>Audit Logs</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/transactions" onClick={()=>setSidebarOpen(false)}>Transactions</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/abandoned-orders" onClick={()=>setSidebarOpen(false)}>Abandoned Orders</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/tickets" onClick={()=>setSidebarOpen(false)}>Support Tickets</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/system-health" onClick={()=>setSidebarOpen(false)}>System Health</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/monitoring" onClick={()=>setSidebarOpen(false)}>Monitoring</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/performance" onClick={()=>setSidebarOpen(false)}>Performance</NavLink>
          </NavItem>

          {/* Critical Admin Tools */}
          <NavItem>
            <NavLink to="/admin/user-management" onClick={()=>setSidebarOpen(false)}>🛠️ User Management Tools</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/subscription-tools" onClick={()=>setSidebarOpen(false)}>📋 Subscription Tools</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/subscription-management" onClick={()=>setSidebarOpen(false)}>🔍 Subscription Management</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/taager-management" onClick={()=>setSidebarOpen(false)}>📦 Taager Integration</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/whatsapp" onClick={()=>setSidebarOpen(false)}>📱 WhatsApp Mass Send</NavLink>
          </NavItem>

          {admin.is_superadmin && (
            <>
              <NavItem>
                <NavLink to="/admin/ai" onClick={()=>setSidebarOpen(false)}>AI Configuration</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/configuration" onClick={()=>setSidebarOpen(false)}>🔧 Configuration Management</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/email-config" onClick={()=>setSidebarOpen(false)}>📧 Email Service Config</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/admins" onClick={()=>setSidebarOpen(false)}>Admin Users</NavLink>
              </NavItem>
            </>
          )}
          <NavItem>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </NavItem>
        </NavMenu>
      </AdminSidebar>
      <MainContent>
        {title && (
          <PageHeader>
            <PageTitle>{title}</PageTitle>
            {description && <PageDescription>{description}</PageDescription>}
          </PageHeader>
        )}
        <Outlet />
      </MainContent>
    </LayoutContainer>
  );
};

export default AdminLayout;